/**
 * atomic-dropdown.js — shared custom dropdown over a native <select>
 * (AAS-1180, Figma 14401-190523/-190535/-190693).
 *
 * The native select stays in the DOM as the state carrier — forms submit it
 * and existing JS keeps hearing its bubbled 'change' — but is hidden by CSS;
 * the visible UI is a trigger button + option listbox popup injected next to
 * it. Enhancement is idempotent and re-runs safely over re-rendered DOM
 * (Lit, section rerenders, province rebuilds).
 *
 * Enhancement targets:
 *   - every `.select > select.select__select` (the theme's canonical native
 *     select component) — opt out per select with [data-native-select]
 *   - any other select marked [data-atomic-dropdown]
 *
 * Consumers with their own construction loop (wishlist modal) call
 *   window.AtomicDropdown.enhance(select)  /  .sync(select)
 * and get the same behavior; per-context sizing is CSS custom props on an
 * ancestor (see atomic-dropdown.css header).
 */
(() => {
  if (window.AtomicDropdown) return;

  const OPEN = 'is-open';
  const CLOSING = 'atomic-dropdown--closing';
  const UP = 'atomic-dropdown--up';
  const STATIC = 'atomic-dropdown--static';
  const FIXED = 'atomic-dropdown--fixed'; // clip-escape mode (see position())
  const OPTION_SELECTOR = '.atomic-dropdown__option:not(.atomic-dropdown__option--disabled)';
  let popupSeq = 0;

  const chevron =
    '<svg class="atomic-dropdown__chevron" xmlns="http://www.w3.org/2000/svg" width="6" height="10" viewBox="0 0 6 10" fill="none" aria-hidden="true"><path d="M5.16406 0.353516L0.70692 4.81066L5.16406 9.2678" stroke="currentColor"/></svg>';

  const escapeHtml = (value) =>
    String(value).replace(/[&<>"']/g, (ch) => (
      { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]
    ));

  const selectSignature = (select) =>
    [...select.options]
      .map((option) => `${option.value}${option.selected ? '*' : ''}${option.disabled ? '!' : ''}`)
      .join('|');

  // The dropdown lives next to its select inside a shared parent; both
  // directions of the pairing go through that parent so Lit/section
  // re-renders that replace either half re-pair naturally. External-trigger
  // mode (select[data-atomic-dropdown="external"]) nests the select inside
  // an existing button, so the pairing scope widens to the marked anchor.
  const scopeFor = (node) => node.closest('.atomic-dropdown-anchor') || node.parentElement;
  const selectFor = (dropdown) => scopeFor(dropdown)?.querySelector('select');
  const dropdownFor = (select) => scopeFor(select)?.querySelector('.atomic-dropdown');
  const triggerFor = (dropdown) =>
    dropdown.querySelector('.atomic-dropdown__trigger') ||
    scopeFor(dropdown)?.querySelector('button[aria-haspopup="listbox"]');

  // The current selection lives in the trigger row and is NOT repeated in
  // the list — the Figma open state. Disabled options render inert.
  const renderOptions = (select) =>
    [...select.options]
      .filter((option) => !option.selected)
      .map((option, index) => {
        const disabled = option.disabled ? ' atomic-dropdown__option--disabled' : '';
        const row =
          `<div class="atomic-dropdown__option${disabled}" role="option" aria-selected="false"` +
          `${option.disabled ? ' aria-disabled="true"' : ' tabindex="0"'}` +
          ` data-value="${escapeHtml(option.value)}">` +
          `<span class="atomic-dropdown__option-label">${escapeHtml(option.textContent.trim())}</span></div>`;
        return index > 0 ? `<div class="atomic-dropdown__divider"></div>${row}` : row;
      })
      .join('');

  // ── Clip escape helpers (see position()) ───────────────────────────────
  // Going position:fixed frees the popup from every scroll container BELOW
  // it, but NOT from an ancestor that both establishes its containing block
  // (transform / perspective / filter / backdrop-filter / contain /
  // will-change) and hides its overflow: that ancestor is the popup's
  // containing block, so its clip still applies. .info-drawer__panel is
  // exactly that — transform for the slide-up, overflow:hidden for the 3px
  // corner clip — which cut the wishlist size dropdown off at the panel
  // edge. Those ancestors get .atomic-dropdown-unclipped (overflow:visible)
  // for as long as an escaped popup is open. Scroll containers below the
  // containing block are deliberately left alone — they no longer clip the
  // popup, and unclipping them would kill their scrolling.
  const UNCLIP = 'atomic-dropdown-unclipped';

  const clipsOverflow = (style) =>
    style.overflowX !== 'visible' || style.overflowY !== 'visible';

  const isFixedContainingBlock = (style) =>
    style.transform !== 'none' ||
    style.perspective !== 'none' ||
    style.filter !== 'none' ||
    (style.backdropFilter || style.webkitBackdropFilter || 'none') !== 'none' ||
    /\b(paint|layout|strict|content)\b/.test(style.contain || '') ||
    /\b(transform|perspective|filter)\b/.test(style.willChange || '');

  let unclipped = [];

  const clearUnclip = () => {
    unclipped.forEach((node) => node.classList.remove(UNCLIP));
    unclipped = [];
  };

  const unclipFor = (dropdown) => {
    clearUnclip();
    let node = dropdown.parentElement;
    // nothing below the containing block clips a fixed popup
    while (node && !isFixedContainingBlock(getComputedStyle(node))) node = node.parentElement;
    for (; node; node = node.parentElement) {
      const style = getComputedStyle(node);
      if (clipsOverflow(style)) {
        node.classList.add(UNCLIP);
        unclipped.push(node);
      }
      // a viewport-anchored ancestor ends the clip chain (.info-drawer is
      // position:fixed) — stop, so page-level overflow on <body>/<html> is
      // never touched
      if (style.position === 'fixed') break;
    }
  };

  // Close plays the reverse animation on --closing, then commits (is-open
  // off → display:none) after it ends. 210ms = the 0.2s close animation +
  // a settle margin.
  const closeTimers = new WeakMap();

  const close = (dropdown) => {
    if (!dropdown.classList.contains(OPEN) || closeTimers.has(dropdown)) return;
    dropdown.classList.add(CLOSING);
    triggerFor(dropdown)?.setAttribute('aria-expanded', 'false');
    closeTimers.set(
      dropdown,
      setTimeout(() => {
        closeTimers.delete(dropdown);
        dropdown.classList.remove(OPEN, CLOSING);
        // kept through the close animation so the popup is not re-clipped
        // mid-flight; dropped once nothing is open any more
        if (!document.querySelector(`.atomic-dropdown.${OPEN}`)) clearUnclip();
      }, 210)
    );
  };

  const open = (dropdown) => {
    const pendingClose = closeTimers.get(dropdown);
    if (pendingClose) {
      clearTimeout(pendingClose);
      closeTimers.delete(dropdown);
    }
    dropdown.classList.remove(CLOSING);
    dropdown.classList.add(OPEN);
    const select = selectFor(dropdown);
    if (select) sync(select);
    triggerFor(dropdown)?.setAttribute('aria-expanded', 'true');
    position(dropdown);
  };

  const closeAll = (except) => {
    document.querySelectorAll(`.atomic-dropdown.${OPEN}`).forEach((dropdown) => {
      if (dropdown !== except) close(dropdown);
    });
  };

  // Nearest ancestor whose overflow actually clips absolutely-positioned
  // children — the popup can't escape it, so flip/cap math runs against it.
  // Falls back to the viewport (position:fixed ancestors et al. are close
  // enough for every current slot; revisit if a transformed ancestor slot
  // appears).
  const clipAncestor = (dropdown) => {
    for (let node = dropdown.parentElement; node; node = node.parentElement) {
      const { overflowY } = getComputedStyle(node);
      if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'hidden') return node;
    }
    return null;
  };

  // Popup drops below the trigger by default; when it would spill past the
  // clip viewport, flip it above — unless above has even less room. Called
  // with .is-open already applied so offsetHeight is measurable.
  const position = (dropdown) => {
    const popup = dropdown.querySelector('.atomic-dropdown__popup');
    const trigger = triggerFor(dropdown);
    if (!popup || !trigger) return;
    // Undo any previous clip-escape placement so the in-flow measurement
    // below starts clean (re-positions run on every ancestor scroll).
    dropdown.classList.remove(FIXED);
    popup.style.position = '';
    popup.style.top = '';
    popup.style.left = '';
    popup.style.bottom = '';
    popup.style.width = '';
    const clipRect = clipAncestor(dropdown)?.getBoundingClientRect();
    const limitTop = Math.max(clipRect ? clipRect.top : 0, 0);
    const limitBottom = Math.min(clipRect ? clipRect.bottom : Infinity, window.innerHeight);
    const triggerRect = trigger.getBoundingClientRect();
    const spaceBelow = limitBottom - triggerRect.bottom;
    const spaceAbove = triggerRect.top - limitTop;
    const keptScrollTop = popup.scrollTop; // clearing max-height clamps it to 0
    popup.style.maxHeight = ''; // measure the natural height
    const popupHeight = popup.offsetHeight;
    const openUp = popupHeight > spaceBelow && (popupHeight <= spaceAbove || spaceAbove > spaceBelow);
    dropdown.classList.toggle(UP, openUp);
    // When even the chosen side is too short, the clip ancestor itself is
    // the problem (short drawer bodies — a two-item wishlist, the
    // back-in-stock summary). Escape it: fixed positioning frees the popup
    // from every scroll container below it (unclipFor() handles the clip its
    // containing block still applies), and the flip decision reruns against
    // the viewport — below the trigger when it fits, above when below is
    // short. Only genuinely clipped popups take this path; every slot with
    // room keeps the plain in-flow placement above. AAS-5067
    const space = openUp ? spaceAbove : spaceBelow;
    if (popupHeight > space) {
      const viewBelow = window.innerHeight - triggerRect.bottom;
      const viewAbove = triggerRect.top;
      const fixedUp =
        popupHeight > viewBelow && (popupHeight <= viewAbove || viewAbove > viewBelow);
      const viewSpace = Math.max((fixedUp ? viewAbove : viewBelow) - 4, 0);
      if (popupHeight > viewSpace) popup.style.maxHeight = `${viewSpace}px`;
      const finalHeight = Math.min(popupHeight, viewSpace);
      dropdown.classList.toggle(UP, fixedUp);
      dropdown.classList.add(FIXED);
      unclipFor(dropdown);
      popup.style.position = 'fixed';
      popup.style.width = `${triggerRect.width}px`;
      popup.style.bottom = 'auto';
      // Containing-block calibration: an ancestor with transform or
      // backdrop-filter (the info-drawer panel/backdrop have both)
      // re-anchors fixed children away from the viewport. Probing the popup
      // itself is unreliable — its open/close animation translates it while
      // in flight — so a throwaway unanimated fixed element measures where
      // fixed 0/0 actually lands, and the popup offsets from that origin.
      // The ±1px mirrors the in-flow seam overlap.
      const probeEl = document.createElement('div');
      probeEl.style.cssText = 'position:fixed;top:0;left:0;width:0;height:0;pointer-events:none;';
      dropdown.appendChild(probeEl);
      const origin = probeEl.getBoundingClientRect();
      probeEl.remove();
      const targetTop = fixedUp ? triggerRect.top - finalHeight + 1 : triggerRect.bottom - 1;
      popup.style.top = `${targetTop - origin.top}px`;
      popup.style.left = `${triggerRect.left - origin.left}px`;
    } else {
      clearUnclip();
    }
    popup.scrollTop = keptScrollTop;
  };

  const sync = (select) => {
    const dropdown = dropdownFor(select);
    if (!dropdown) return;
    const signature = selectSignature(select);
    // Hidden country selectors can each duplicate hundreds of options into
    // the DOM. Build the list on first open; keep an open list synchronized.
    if (dropdown.classList.contains(OPEN) && dropdown.dataset.signature !== signature) {
      dropdown.dataset.signature = signature;
      dropdown.querySelector('.atomic-dropdown__popup').innerHTML = renderOptions(select);
    }
    // The listbox never repeats the current value, so a single-option select
    // would open an EMPTY popup. Render it as a static value chip instead —
    // trigger disabled, chevron hidden via the modifier class.
    const isStatic = select.options.length <= 1;
    dropdown.classList.toggle(STATIC, isStatic);
    const trigger = dropdown.querySelector('.atomic-dropdown__trigger');
    if (trigger) trigger.disabled = isStatic || select.disabled;
    if (isStatic) close(dropdown);
    const selected = select.options[select.selectedIndex];
    // external mode has no trigger of its own — the host button keeps its
    // static label ("Sort") and only carries the aria state
    const labelEl = dropdown.querySelector('.atomic-dropdown__trigger-label');
    if (labelEl) {
      const label = selected ? selected.textContent.trim() : '';
      if (labelEl.textContent !== label) labelEl.textContent = label;
    }
  };

  // Province/variant rebuilds mutate the option list without firing
  // 'change' — mirror them into the popup as they happen.
  const optionObserver = new MutationObserver((mutations) => {
    const seen = new Set();
    mutations.forEach(({ target }) => {
      const select = target instanceof Element ? target.closest('select') : null;
      if (select && !seen.has(select)) {
        seen.add(select);
        sync(select);
      }
    });
  });

  const enhance = (select) => {
    if (!(select instanceof HTMLSelectElement)) return;
    let dropdown = dropdownFor(select);
    if (!dropdown) {
      const external = select.dataset.atomicDropdown === 'external';
      dropdown = document.createElement('div');
      dropdown.className = external ? 'atomic-dropdown atomic-dropdown--external' : 'atomic-dropdown';
      const popupId = `atomic-dropdown-popup-${popupSeq += 1}`;
      if (external) {
        // A pre-existing button (marked ancestor: .atomic-dropdown-anchor)
        // is the trigger — a trigger of our own nested inside it would be
        // interactive-inside-interactive. Only the popup is injected, as
        // the button's sibling, and the button carries the aria state.
        const anchor = select.closest('.atomic-dropdown-anchor');
        const host = anchor?.querySelector('button');
        if (!host) return;
        dropdown.innerHTML = `<div class="atomic-dropdown__popup" id="${popupId}" role="listbox"></div>`;
        host.setAttribute('aria-haspopup', 'listbox');
        host.setAttribute('aria-expanded', 'false');
        host.setAttribute('aria-controls', popupId);
        host.insertAdjacentElement('afterend', dropdown);
        anchor.classList.add('atomic-dropdown-host');
      } else {
        // accessible name: the wired-up <label>, else "options[Color]" → "Color"
        const labelText =
          select.labels?.[0]?.textContent.trim() ||
          (select.name || '').replace(/^options\[/, '').replace(/\]$/, '');
        dropdown.innerHTML =
          `<button type="button" class="atomic-dropdown__trigger" aria-haspopup="listbox" aria-expanded="false"` +
          ` aria-controls="${popupId}" aria-label="${escapeHtml(labelText)}">` +
          `<span class="atomic-dropdown__trigger-label"></span>${chevron}</button>` +
          `<div class="atomic-dropdown__popup" id="${popupId}" role="listbox"></div>`;
        select.insertAdjacentElement('afterend', dropdown);
        select.parentElement.classList.add('atomic-dropdown-host');
      }
      optionObserver.observe(select, { childList: true, subtree: true, attributes: true });
    }
    sync(select);
  };

  const enhanceAll = (root = document) => {
    root
      .querySelectorAll('.select > select.select__select:not([data-native-select]), select[data-atomic-dropdown]')
      .forEach(enhance);
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('.atomic-dropdown__trigger, .atomic-dropdown-anchor button');
    if (trigger) {
      const dropdown = trigger.closest('.atomic-dropdown') || scopeFor(trigger)?.querySelector('.atomic-dropdown');
      if (!dropdown) return;
      // mid-close counts as closed — a click then reopens instead of eating itself
      const willOpen =
        !dropdown.classList.contains(OPEN) || dropdown.classList.contains(CLOSING);
      closeAll(dropdown);
      if (willOpen) open(dropdown);
      else close(dropdown);
      return;
    }

    const option = event.target.closest('.atomic-dropdown__option');
    if (option) {
      const dropdown = option.closest('.atomic-dropdown');
      if (!option.classList.contains('atomic-dropdown__option--disabled')) {
        const select = selectFor(dropdown);
        if (select && select.value !== option.dataset.value) {
          select.value = option.dataset.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
        if (select) sync(select);
      }
      close(dropdown);
      // hand focus back to the trigger — the picked option is removed from
      // the list (or about to close), so keyboard users need a landing spot;
      // :focus-visible keeps the ring away from mouse picks
      triggerFor(dropdown)?.focus();
      return;
    }

    if (!event.target.closest('.atomic-dropdown')) closeAll();
  });

  // Consumers (and browsers restoring form state) can move the select
  // programmatically — mirror any change back into the custom UI.
  document.addEventListener('change', (event) => {
    if (event.target instanceof HTMLSelectElement && dropdownFor(event.target)) {
      sync(event.target);
    }
  });

  // Capture phase: Escape must win against drawers/modals that also listen
  // for it on document — in bubble order whoever registered first would fire
  // regardless of the dropdown being the innermost open layer.
  document.addEventListener(
    'keydown',
    (event) => {
      if (event.key === 'Escape') {
        const openDropdown = document.querySelector(`.atomic-dropdown.${OPEN}`);
        if (openDropdown) {
          close(openDropdown);
          triggerFor(openDropdown)?.focus();
          event.stopPropagation();
          event.preventDefault();
        }
        return;
      }
      // Listbox arrow navigation (WAI-ARIA): arrows rove focus through the
      // enabled options of the open dropdown; on a closed trigger they open
      // it and land on the first/last option. Home/End jump.
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Home' || event.key === 'End') {
        if (!(event.target instanceof Element)) return;
        const openDropdown = document.querySelector(`.atomic-dropdown.${OPEN}`);
        if (!openDropdown) {
          const trigger = event.target.closest('.atomic-dropdown__trigger, .atomic-dropdown-anchor button');
          if (!trigger || (event.key !== 'ArrowDown' && event.key !== 'ArrowUp')) return;
          const dropdown =
            trigger.closest('.atomic-dropdown') || scopeFor(trigger)?.querySelector('.atomic-dropdown');
          if (!dropdown || dropdown.classList.contains(STATIC)) return;
          event.preventDefault();
          open(dropdown);
          const options = dropdown.querySelectorAll(OPTION_SELECTOR);
          (event.key === 'ArrowDown' ? options[0] : options[options.length - 1])?.focus();
          return;
        }
        const options = [...openDropdown.querySelectorAll(OPTION_SELECTOR)];
        if (!options.length) return;
        event.preventDefault();
        const idx = options.indexOf(document.activeElement);
        let next;
        if (event.key === 'Home') next = options[0];
        else if (event.key === 'End') next = options[options.length - 1];
        else if (event.key === 'ArrowDown') next = idx < 0 ? options[0] : options[Math.min(idx + 1, options.length - 1)];
        else next = idx < 0 ? options[options.length - 1] : options[Math.max(idx - 1, 0)];
        next.focus();
        return;
      }
      if (event.key !== 'Enter' && event.key !== ' ') return;
      const option = event.target instanceof Element && event.target.closest('.atomic-dropdown__option');
      if (option) {
        event.preventDefault();
        option.click();
      }
    },
    true
  );

  // Any ancestor scrolling moves an open dropdown relative to its clip
  // viewport, so the fits-below verdict can change mid-flight — re-measure.
  // Capture phase: scroll events don't bubble.
  document.addEventListener(
    'scroll',
    (event) => {
      // the popup's own internal scroll must not trigger a re-measure — the
      // max-height reset inside position() would clamp scrollTop to 0
      if (event.target instanceof Element && event.target.closest('.atomic-dropdown__popup')) return;
      const openDropdown = document.querySelector(`.atomic-dropdown.${OPEN}`);
      if (openDropdown) position(openDropdown);
    },
    true
  );

  // Leaving an open dropdown with a mouse closes it (opening stays
  // click-only; touch keeps the outside-tap close instead).
  document.addEventListener('pointerout', (event) => {
    if (!(event.target instanceof Element)) return;
    const openDropdown = event.target.closest(`.atomic-dropdown.${OPEN}`);
    if (openDropdown && event.pointerType !== 'touch' && !openDropdown.contains(event.relatedTarget)) {
      close(openDropdown);
    }
  });

  window.AtomicDropdown = { enhance, enhanceAll, sync, close, closeAll };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => enhanceAll());
  } else {
    enhanceAll();
  }

  // Shopify section/theme-editor rerenders re-inject selects without a page
  // load — re-enhance whatever arrived.
  document.addEventListener('shopify:section:load', (event) => enhanceAll(event.target));
})();
